import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { BRANCHES, formatIST } from "@/lib/branches";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Download, FileSpreadsheet } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function downloadExcel(day) {
  // day: "today" | "yesterday"
  const url = `${API_BASE}/api/registrations/export?day=${day}&filter_by=created&pin=1234`;
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function RegistrationsTab() {
  const [branch, setBranch] = useState(BRANCHES[0]);
  const [regs, setRegs] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/registrations", { params: { branch } });
      setRegs(data.registrations || []);
    } catch {
      setRegs([]);
    }
  };

  useEffect(() => { load(); }, [branch]);

  const clearAll = async () => {
    try {
      await apiClient.delete("/registrations", { params: { branch, pin: "1234" } });
      toast.success("Cleared registrations for " + branch);
      setOpenConfirm(false);
      load();
    } catch {
      toast.error("Failed to clear");
    }
  };

  return (
    <div className="space-y-6" data-testid="registrations-tab">
      {/* Excel download strip */}
      <div className="kg-card kg-card-red-top p-5" data-testid="excel-download-strip">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#e8f7ec" }}>
            <FileSpreadsheet className="w-5 h-5 text-green-700" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-neutral-900">Download registrations as Excel</p>
            <p className="text-sm text-neutral-600 mt-0.5">
              Full customer list with name, phone, Instagram handle, timestamp and branch — filtered by the day they registered (IST).
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                onClick={() => downloadExcel("today")}
                className="h-11 bg-green-600 hover:bg-green-700 text-white"
                data-testid="download-excel-today"
              >
                <Download className="w-4 h-4 mr-2" /> Download Today&apos;s Registrations
              </Button>
              <Button
                onClick={() => downloadExcel("yesterday")}
                variant="outline"
                className="h-11 border-green-600 text-green-700 hover:bg-green-50"
                data-testid="download-excel-yesterday"
              >
                <Download className="w-4 h-4 mr-2" /> Download Yesterday&apos;s Registrations
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="kg-card kg-card-red-top p-6">
        <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-4 justify-between">
          <div className="flex-1">
            <label className="text-sm text-neutral-700">Branch</label>
            <Select value={branch} onValueChange={setBranch}>
              <SelectTrigger className="kg-input mt-2 h-12 border-2" data-testid="regs-branch-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BRANCHES.map((b, i) => (
                  <SelectItem key={i} value={b} data-testid={`regs-branch-opt-${i}`}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3">
            <span className="kg-badge-red" data-testid="regs-count">{regs.length} entries</span>
            <Button
              variant="outline"
              className="border-[#CC0000] text-[#CC0000] hover:bg-[#FFE9E9]"
              disabled={regs.length === 0}
              onClick={() => setOpenConfirm(true)}
              data-testid="clear-all-btn"
            >
              Clear All
            </Button>
          </div>
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-neutral-500 border-b border-neutral-200">
                <th className="py-2 pr-4 w-12">#</th>
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Phone</th>
                <th className="py-2 pr-4">Invoice #</th>
                <th className="py-2 pr-4">Instagram</th>
                <th className="py-2 pr-4">Time</th>
              </tr>
            </thead>
            <tbody>
              {regs.length === 0 ? (
                <tr><td colSpan={6} className="py-6 text-center text-neutral-500" data-testid="regs-empty">No registrations yet for this branch today.</td></tr>
              ) : regs.map((r, i) => (
                <tr key={r.id} className="border-b border-neutral-100" data-testid={`reg-row-${i}`}>
                  <td className="py-2 pr-4 text-neutral-500">{i + 1}</td>
                  <td className="py-2 pr-4 font-medium">{r.name}</td>
                  <td className="py-2 pr-4 font-mono">{r.phone}</td>
                  <td className="py-2 pr-4 font-mono text-xs">{r.invoice_number || "—"}</td>
                  <td className="py-2 pr-4">
                    {r.instagram_username ? (
                      <a
                        href={`https://www.instagram.com/${r.instagram_username}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#CC0000] hover:underline"
                      >@{r.instagram_username}</a>
                    ) : <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="py-2 pr-4 text-neutral-500">{formatIST(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={openConfirm} onOpenChange={setOpenConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all registrations?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all today&apos;s registrations for <b>{branch}</b> and remove its winner if drawn. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="clear-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={clearAll} data-testid="clear-confirm" className="bg-[#CC0000] hover:bg-[#990000]">Yes, Clear</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
